import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import axios from 'axios';
import {
  DEFAULT_OPTIONS,
  initialPlannerState,
  plannerReducer,
} from './plannerReducer';
import { FailedLeg, PlannerState, RouteOptions, RouteResult, Waypoint } from './types';
import {
  buildDirectionsBody,
  fetchDirections,
  reverseGeocode,
} from './api/routingApi';

interface ProbeOutcome {
  route: RouteResult | null;
  failedLegs: FailedLeg[];
  offsets: number[];
}

/**
 * Mapbox reports a failed multi-stop route as one NoRoute for the whole
 * request, so re-run each leg on its own to find the culprit. Whatever still
 * routes is stitched into a partial route; the rest becomes a dashed gap.
 */
async function probeLegs(
  waypoints: Waypoint[],
  options: RouteOptions,
  signal: AbortSignal,
): Promise<ProbeOutcome> {
  const pairs = waypoints.slice(0, -1).map((from, i) => [from, waypoints[i + 1]] as const);
  const settled = await Promise.all(pairs.map(async ([from, to]) => {
    try {
      const body = buildDirectionsBody([from, to], {
        profile: options.profile,
        exclude: options.exclude as unknown as Record<string, boolean>,
        departAt: options.departAt,
      });
      const result = await fetchDirections({ ...body, alternatives: false }, signal);
      return result.code === 'Ok' && result.routes[0] ? result.routes[0] : null;
    } catch {
      return null;
    }
  }));

  const coordinates: [number, number][] = [];
  const legs: RouteResult['legs'] = [];
  const congestion: (number | null)[] = [];
  const offsets: number[] = [0];
  const failedLegs: FailedLeg[] = [];
  let distance = 0;
  let duration = 0;
  let anyCongestion = false;

  settled.forEach((route, index) => {
    if (!route) {
      failedLegs.push({
        fromIndex: index,
        toIndex: index + 1,
        message: 'No road route for this leg.',
      });
      offsets.push(Math.max(0, coordinates.length - 1));
      return;
    }
    const coords = (route.geometry.coordinates || []) as [number, number][];
    const skipFirst = coordinates.length > 0;
    coordinates.push(...(skipFirst ? coords.slice(1) : coords));
    legs.push(...route.legs);
    if (route.congestion) {
      anyCongestion = true;
      congestion.push(...route.congestion);
    } else {
      congestion.push(...new Array(Math.max(0, coords.length - 1)).fill(null));
    }
    distance += route.distance;
    duration += route.duration;
    offsets.push(Math.max(0, coordinates.length - 1));
  });

  if (coordinates.length < 2) {
    return { route: null, failedLegs, offsets };
  }

  return {
    route: {
      id: 'route-partial',
      distance,
      duration,
      geometry: { type: 'LineString', coordinates },
      legs,
      congestion: anyCongestion ? congestion : undefined,
    },
    failedLegs,
    offsets,
  };
}

const ROUTE_DEBOUNCE_MS = 300;
const LABEL_DEBOUNCE_MS = 600;

export interface UsePlannerOptions {
  initialWaypoints?: Waypoint[];
  initialOptions?: Partial<RouteOptions>;
  initialPrimaryName?: string;
  initialAlternateName?: string;
  restoreAlternateKey?: string | null;
  onError?: (message: string | null) => void;
}

export interface UsePlannerResult {
  state: PlannerState;
  /** Per-route vertex offsets, used by drag-to-insert. */
  legOffsets: number[][];
  dispatch: React.Dispatch<Parameters<typeof plannerReducer>[1]>;
  canUndo: boolean;
  canRedo: boolean;
  retry: () => void;
}

/**
 * Owns the planner reducer plus the two side effects it needs: routing
 * (debounced, cancellable, last-write-wins) and lazy reverse geocoding.
 */
export function usePlanner({
  initialWaypoints,
  initialOptions,
  initialPrimaryName,
  initialAlternateName,
  restoreAlternateKey,
  onError,
}: UsePlannerOptions = {}): UsePlannerResult {
  const [state, dispatch] = useReducer(
    plannerReducer,
    undefined,
    () => initialPlannerState({
      waypoints: initialWaypoints || [],
      options: { ...DEFAULT_OPTIONS, ...initialOptions, exclude: { ...DEFAULT_OPTIONS.exclude, ...initialOptions?.exclude } },
      primaryName: initialPrimaryName || '',
      alternateName: initialAlternateName || '',
      restoreAlternateKey: restoreAlternateKey ?? null,
    }),
  );

  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const legOffsetsRef = useRef<number[][]>([]);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const routeKey = useMemo(() => JSON.stringify({
    coords: state.waypoints.map((w) => [w.longitude.toFixed(6), w.latitude.toFixed(6)]),
    profile: state.options.profile,
    exclude: state.options.exclude,
    departAt: state.options.departAt,
  }), [state.waypoints, state.options]);

  const requestRoutes = useCallback(async () => {
    if (state.waypoints.length < 2) {
      legOffsetsRef.current = [];
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    seqRef.current += 1;
    const seq = seqRef.current;
    dispatch({ type: 'ROUTE_START', seq });

    try {
      const body = buildDirectionsBody(state.waypoints, {
        profile: state.options.profile,
        exclude: state.options.exclude as unknown as Record<string, boolean>,
        departAt: state.options.departAt,
      });
      const result = await fetchDirections(body, controller.signal);
      if (seq !== seqRef.current) return;

      if (result.code !== 'Ok' || result.routes.length === 0) {
        const probe = state.waypoints.length > 2
          ? await probeLegs(state.waypoints, state.options, controller.signal)
          : { route: null, failedLegs: [], offsets: [] as number[] };
        if (seq !== seqRef.current) return;

        if (probe.route) {
          legOffsetsRef.current = [probe.offsets];
          dispatch({ type: 'ROUTE_SUCCESS', seq, routes: [probe.route], failedLegs: probe.failedLegs });
          onErrorRef.current?.(null);
          return;
        }

        legOffsetsRef.current = [];
        dispatch({
          type: 'ROUTE_EMPTY',
          seq,
          message: result.message
            || 'No route found between these waypoints. Move them closer to a road or change the profile.',
        });
        onErrorRef.current?.(null);
        return;
      }

      legOffsetsRef.current = result.legOffsets;
      dispatch({ type: 'ROUTE_SUCCESS', seq, routes: result.routes });
      onErrorRef.current?.(null);
    } catch (e: any) {
      if (axios.isCancel(e) || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
      if (seq !== seqRef.current) return;
      const message = e?.response?.data?.detail || e?.message || 'Routing request failed';
      dispatch({ type: 'ROUTE_ERROR', seq, message, retryable: true });
      onErrorRef.current?.(message);
    }
  }, [state.waypoints, state.options]);

  useEffect(() => {
    if (state.waypoints.length < 2) return undefined;
    const timer = window.setTimeout(requestRoutes, ROUTE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // routeKey collapses coordinate/option changes into one trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Lazy labels: batch every waypoint that has never been named.
  const pendingLabelIds = useMemo(
    () => state.waypoints.filter((w) => w.labelStatus === 'idle').map((w) => w.id),
    [state.waypoints],
  );

  useEffect(() => {
    if (pendingLabelIds.length === 0) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      const targets = state.waypoints.filter((w) => pendingLabelIds.includes(w.id));
      if (targets.length === 0) return;
      dispatch({ type: 'LABEL_LOADING', ids: targets.map((t) => t.id) });
      try {
        const results = await reverseGeocode(
          targets.map((t) => ({ longitude: t.longitude, latitude: t.latitude })),
          controller.signal,
        );
        dispatch({
          type: 'LABEL_RESOLVED',
          labels: targets.map((t, i) => ({
            id: t.id,
            label: results[i]?.label,
            address: results[i]?.address,
          })),
        });
      } catch {
        dispatch({ type: 'LABEL_RESOLVED', labels: targets.map((t) => ({ id: t.id })) });
      }
    }, LABEL_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLabelIds.join(',')]);

  return {
    state,
    legOffsets: legOffsetsRef.current,
    dispatch,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    retry: requestRoutes,
  };
}
